import React, { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { CopyableAddress } from '../common/CopyButton';

const PrivateKeyExport = ({ wallet, onClose }) => {
  const { showSuccess, showError, showWarning } = useToast();
  
  const [password, setPassword] = useState('');
  const [privateKeyData, setPrivateKeyData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [step, setStep] = useState('password'); // password, export, complete

  const handleExportPrivateKey = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      showWarning('Please enter your password');
      return;
    }

    setIsLoading(true);
    
    try {
      const tokensData = localStorage.getItem('tokens');
      const tokens = tokensData ? JSON.parse(tokensData) : null;
      const token = tokens?.access_token;
      
      console.log('Tokens data:', tokensData ? 'Found' : 'Not found');
      console.log('Access token:', token ? token.substring(0, 20) + '...' : 'No access token found');
      
      if (!token) {
        throw new Error('You must be logged in to export private keys. Please log in first.');
      }
      
      const baseUrl = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';
      const response = await fetch(`${baseUrl}/wallets/${wallet.id}/export-private-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.log('Error response:', errorData);
        
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        }
        throw new Error(`Server error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      console.log('Success response:', data);

      if (data.success) {
        setPrivateKeyData(data.data.export_data);
        setStep('export');
        showSuccess('Private key exported successfully');
      } else {
        throw new Error(data.error?.message || 'Failed to export private key');
      }
    } catch (error) {
      console.error('Error exporting private key:', error);
      showError(error.message || 'Failed to export private key. Please check your password.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(`${label} copied to clipboard`);
    } catch (error) {
      showError('Failed to copy to clipboard');
    }
  };

  const handleComplete = () => {
    setStep('complete');
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  if (step === 'complete') {
    return (
      <div className="modal-overlay">
        <div className="modal-content private-key-modal">
          <div className="completion-message">
            <div className="success-icon">✅</div>
            <h3>Export Complete</h3>
            <p>Your private key has been exported successfully.</p>
            <p className="security-reminder">Remember to keep it secure!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content private-key-modal">
        <div className="modal-header">
          <h3>Export Private Key</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {step === 'password' && (
          <div className="password-step">
            <div className="security-warning">
              <div className="warning-icon">⚠️</div>
              <div className="warning-content">
                <h4>Security Warning</h4>
                <ul>
                  <li>Your private key gives full control over your wallet</li>
                  <li>Never share it with anyone</li>
                  <li>Store it securely offline</li>
                  <li>Anyone with access can steal your funds</li>
                </ul>
              </div>
            </div>

            <div className="wallet-info">
              <h4>Wallet Details</h4>
              <div className="wallet-detail">
                <span className="label">Address:</span>
                <div className="value">
                  <CopyableAddress 
                    address={wallet.wallet_address}
                    truncate={false}
                  />
                </div>
              </div>
              <div className="wallet-detail">
                <span className="label">Type:</span>
                <span className="value">Platform Wallet</span>
              </div>
            </div>

            <form onSubmit={handleExportPrivateKey} className="password-form">
              <div className="form-group">
                <label htmlFor="password">Enter Your Account Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password to confirm"
                  required
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-actions">
                <button type="button" onClick={onClose} disabled={isLoading}>
                  Cancel
                </button>
                <button type="submit" disabled={isLoading} className="export-button">
                  {isLoading ? 'Exporting...' : 'Export Private Key'}
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 'export' && privateKeyData && (
          <div className="export-step">
            <div className="export-success">
              <div className="success-icon">🔑</div>
              <h4>Private Key Exported</h4>
              <p>Your private key is ready. Choose how you want to view it:</p>
            </div>

            <div className="private-key-section">
              <div className="key-format-info">
                <h5>Format: Base58 (Phantom Compatible)</h5>
                <p>This format can be directly imported into Phantom wallet</p>
              </div>

              <div className="private-key-display">
                <div className="key-container">
                  {showPrivateKey ? (
                    <div className="key-revealed">
                      <textarea
                        readOnly
                        value={privateKeyData.private_key_base58}
                        className="private-key-text"
                        rows="3"
                      />
                      <div className="key-actions">
                        <button
                          onClick={() => copyToClipboard(privateKeyData.private_key_base58, 'Private key')}
                          className="copy-key-button"
                        >
                          📋 Copy Private Key
                        </button>
                        <button
                          onClick={() => setShowPrivateKey(false)}
                          className="hide-key-button"
                        >
                          👁️ Hide Key
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="key-hidden">
                      <div className="hidden-key-placeholder">
                        🔒 Private key is hidden for security
                      </div>
                      <button
                        onClick={() => setShowPrivateKey(true)}
                        className="reveal-key-button"
                      >
                        👁️ Reveal Private Key
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="import-instructions">
                <h5>How to Import into Phantom:</h5>
                <ol>
                  <li>Open Phantom wallet</li>
                  <li>Click "Add / Connect Wallet"</li>
                  <li>Select "Import Private Key"</li>
                  <li>Paste the private key above</li>
                  <li>Give your wallet a name</li>
                </ol>
              </div>

              <div className="final-warning">
                <div className="warning-icon">🚨</div>
                <div className="warning-text">
                  <strong>Final Reminder:</strong> This private key gives complete control over your wallet. 
                  Store it securely and never share it with anyone.
                </div>
              </div>

              <div className="completion-actions">
                <button onClick={onClose} className="close-button">
                  Close
                </button>
                <button onClick={handleComplete} className="done-button">
                  I've Saved It Securely
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivateKeyExport;